import type {Database} from '$lib/classes/interfaces/database';
import type {User} from 'firebase/auth';
import Sqids from 'sqids';
import type {ColorScheme} from '$lib/classes/_types/color-scheme';
import type {ThemeManager} from '$lib/classes/services/theme-manager';
import {PlaybackRate} from '$lib/classes/_types/playback-rate';
import {timer} from '$lib/utils/timer';

export type AddWordsBatchSizeOption = [number, number, number];

export type ReferralInfo = {
    by: string | null,
    code: string,
}

export type UserSettingsDoc = {
    theme: ColorScheme | null,
    selectedColor: string,
    colorCodeNouns: boolean,
    nounColors: {
        maskulinum: string,
        femininum: string,
        neutrum: string
    },
    playbackControls: {
        enabled: boolean,
        rate: PlaybackRate,
    },
    showSingularPluralOfNouns: boolean,
    showVerbConjugation: boolean,
    wordsPerSession: {
        limit: boolean,
        count: number,
    },
    randomizeWordOrder: boolean,
    autofocusInput: boolean,
    addWordsCounts: AddWordsBatchSizeOption,
    reminder: {
        timezone: string,
        emailHour: number,
        sendEmail: boolean,
    },
    referral?: ReferralInfo
}

export class UserSettings {

    private _current = $state(UserSettings.defaultValue)

    private uid: string | null = null
    private unsubscribeUserSettingsDoc: () => void = () => {}

    constructor(
        private readonly database: Database,
        private readonly themeManager: ThemeManager,
    ) {}

    private static get defaultValue(): UserSettingsDoc {
        return {
            theme: null,
            selectedColor: 'var(--color-Verb)',
            colorCodeNouns: false,
            nounColors: {
                maskulinum: '#103e96',
                femininum: '#852276',
                neutrum: '#1e6c1e'
            },
            playbackControls: {
                enabled: true,
                rate: PlaybackRate.Normal,
            },
            showSingularPluralOfNouns: false,
            showVerbConjugation: false,
            wordsPerSession: {
                limit: false,
                count: 50,
            },
            randomizeWordOrder: false,
            autofocusInput: false,
            addWordsCounts: [10, 25, 50],
            reminder: {
                timezone: '',
                emailHour: 8,
                sendEmail: false,
            }
        }
    }

    private getInitialUserSettingsDoc(user: User) {
        // NOTE User is missing createdAt, but it's present on instance UserImpl (internal)
        //  use if present since it's more precise (creationTime is missing milliseconds)
        // @ts-ignore
        const createdAt: string = user.metadata.createdAt
        if (!createdAt) console.error('ERROR User.metadata.createdAt missing >> falling back to .creationTime')
        const creationTime = user.metadata.creationTime
        const creationTimestamp = createdAt ? +createdAt : creationTime ? new Date(creationTime).getTime() : null
        if (!creationTimestamp) return {}
        try {
            const sqids = new Sqids({
                alphabet: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
            })
            const initialDoc = {
                referral: {
                    code: sqids.encode([creationTimestamp]),
                    by: localStorage.getItem('ref') ?? null,
                }
            }
            localStorage.removeItem('ref')
            return initialDoc
        } catch (error) {
            console.error('ERROR creating initialSettingsDoc', error)
            return {}
        }
    }

    async init(user: User): Promise<void> {
        const timerUserSettings = timer('userSettings')

        const uid = user.uid
        this.uid = uid

        let savedSettingsDoc: Partial<UserSettingsDoc> | null

        try {
            savedSettingsDoc = await this.database.getUserSettingsDoc(uid)
        } catch (error) {
            throw new Error('ERROR userSettings.init() - getUserSettingsDoc', {cause: error})
        }

        try {
            if (savedSettingsDoc === null) {
                // NOTE could be improved by passing referrer to initialDoc method and only delete after successful write
                await this.database.setUserSettingsDoc(uid, this.getInitialUserSettingsDoc(user))
            }
        } catch (error) {
            throw new Error('ERROR userSettings.init() - setUserSettingsDoc', {cause: error})
        }

        const {
            firstValue,
            unsubscribe
        } = this.database.subscribeUserSettingsDoc(uid, (userSettingsDoc: Partial<UserSettingsDoc> | null) => {
            if (userSettingsDoc) {
                this.themeManager.set(userSettingsDoc.theme)
                this._current = {...this._current, ...userSettingsDoc}
            } else {
                console.error('ERROR userSettings doc is null in doc subscription callback - should never happen')
            }
        })

        this.unsubscribeUserSettingsDoc = unsubscribe

        try {
            await firstValue // the first value is handled via callback
        } catch (error) {
            throw new Error('ERROR userSettings.init() - subscribeUserSettingsDoc', {cause: error})
        }

        timerUserSettings()
    }

    get<K extends keyof UserSettingsDoc>(key: K): UserSettingsDoc[K] {
        return this._current[key]
    }

    async changeSetting<K extends keyof UserSettingsDoc>(key: K, value: UserSettingsDoc[K]) {
        try {
            this._current[key] = value

            if (key === 'theme') {
                this.themeManager.set(value as UserSettingsDoc['theme'])
            }

            if (this.uid) {
                await this.database.setUserSettingsDoc(this.uid, {[key]: value})
            }
        } catch (error) {
            console.error('ERROR while changing userSetting', error)
        }
    }

    clear() {
        this.unsubscribeUserSettingsDoc()
        this._current = UserSettings.defaultValue
        this.uid = null
    }
}